import type { ViewState } from './list-api.ts';
import type { ListSelection } from './selection-bar.tsx';

/**
 * Gedächtnis je Liste für diese Sitzung im Browser (Story 1.23, EXPERIENCE §10.2): Am Handy öffnet eine Karte den
 * Datensatz als eigene Seite; „Zurück“ führt an dieselbe Ansicht und Scrollstelle, die Pfeile im Kopf springen zum
 * vorigen bzw. nächsten Datensatz der Liste. Die Listen-Vorlieben (zuletzt genutzte Ansicht, Entwurf) bleiben
 * maßgeblich beim Server; hier liegt nur, was eine Seite überleben muss: gewählte Ansicht, Arbeitsstand, Auswahl-Modus,
 * Reihenfolge der geladenen Zeilen und die Scrollstelle. Getrennt je Person (`owner` = Benutzer-ID): Meldet sich jemand
 * anderes im selben Browser an, beginnt er leer; Abmelden löscht alles (`clearListMemory`).
 */
export interface ListMemory {
  chosenId: string | null;
  local: Record<string, ViewState | null>;
  /** Auswahl-Modus am Handy und die Auswahl selbst. */
  selecting: boolean;
  selection: ListSelection | null;
  /** Schlüssel der geladenen Zeilen in Anzeigereihenfolge (für ↑ ↓ am Datensatz). */
  order: string[];
  /** Scrollstelle beim Öffnen eines Datensatzes; beim nächsten Anzeigen der Liste einmal wiederhergestellt. */
  scrollY: number | null;
  /** Der Datensatz wurde aus der Liste geöffnet („Zurück“ geht dann im Verlauf zurück). */
  openedFromList: boolean;
  /** Schnellsuche (Story 1.32; nicht Teil der Ansicht). */
  search?: string;
  /** Neue Zeilen einer beobachteten Ansicht markieren (Story 1.34: Kennung des Treffers aus der Glocke). */
  watchHit?: string | null;
}

const memory = new Map<string, ListMemory>();
/** Person, der der Inhalt gehört; ein Wechsel leert das Gedächtnis. */
let currentOwner: string | null = null;

function forOwner(owner: string): Map<string, ListMemory> {
  if (owner !== currentOwner) {
    memory.clear();
    currentOwner = owner;
  }
  return memory;
}

/** Alles vergessen (Abmelden). */
export function clearListMemory(): void {
  memory.clear();
  currentOwner = null;
}

const empty = (): ListMemory => ({ chosenId: null, local: {}, selecting: false, selection: null, order: [], scrollY: null, openedFromList: false });

export function readListMemory(owner: string, list: string): ListMemory {
  return forOwner(owner).get(list) ?? empty();
}

export function updateListMemory(owner: string, list: string, patch: Partial<ListMemory>): void {
  forOwner(owner).set(list, { ...readListMemory(owner, list), ...patch });
}

/** Vorheriger und nächster Datensatz in der zuletzt gezeigten Reihenfolge der Liste (`null` = keiner). */
export function listNeighbors(owner: string, list: string, key: string): { prev: string | null; next: string | null } {
  const order = readListMemory(owner, list).order;
  const i = order.indexOf(key);
  if (i < 0) return { prev: null, next: null };
  return { prev: order[i - 1] ?? null, next: order[i + 1] ?? null };
}
